/**
 * Utility to extract and collect webpage context
 * Can be used with any webpage to provide context to the Copilot
 */

/**
 * Extract main content from the page
 * Removes script, style, nav, footer, and other non-content elements
 */
export function extractPageContent(maxLength = 2000) {
  // Clone the body to avoid modifying the original
  const clone = document.body.cloneNode(true);

  // Remove script and style elements
  const elementsToRemove = clone.querySelectorAll(
    'script, style'
  );

  elementsToRemove.forEach((el) => el.remove());

  // Get text content
  let text = clone.innerText || clone.textContent || '';

  // Clean up whitespace
  text = text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join('\n');

  // Truncate if too long
  if (text.length > maxLength) {
    text = text.substring(0, maxLength) + '...';
  }

  return text;
}

/**
 * Extract page metadata
 */
export function extractMetadata() {
  const metadata = {};

  const metas = document.querySelectorAll('meta');

  metas.forEach((meta) => {
    const name =
      meta.getAttribute('name') ||
      meta.getAttribute('property');

    const content = meta.getAttribute('content');

    if (name && content) {
      metadata[name] = content;
    }
  });

  return metadata;
}

/**
 * Extract structured data (JSON-LD, microdata, etc.)
 */
export function extractStructuredData() {
  const structured = {};

  // Extract JSON-LD
  const jsonLdScripts = document.querySelectorAll(
    'script[type="application/ld+json"]'
  );

  jsonLdScripts.forEach((script, index) => {
    try {
      const data = JSON.parse(script.textContent || '{}');
      structured[`jsonld_${index}`] = data;
    } catch {
      // Ignore parse errors
    }
  });

  return structured;
}

/**
 * Collect all available page context
 */
export function collectPageContext(options = {}) {
  const {
    maxContentLength = 2000,
    includeMetadata = true,
    includeStructuredData = true
  } = options;

  const context = {
    title: document.title,
    url: window.location.href,
    description:
      document
        .querySelector('meta[name="description"]')
        ?.getAttribute('content') || undefined,
    content: extractPageContent(maxContentLength)
  };

  if (includeMetadata) {
    context.metadata = extractMetadata();
  }

  if (includeStructuredData) {
    context.structuredData = extractStructuredData();
  }

  return context;
}

/**
 * Collect only essential page context (lightweight)
 * Good for quick responses when full context is not needed
 */
export function collectLightPageContext() {
  return {
    title: document.title,
    url: window.location.href,
    description:
      document
        .querySelector('meta[name="description"]')
        ?.getAttribute('content') || undefined,
    content: extractPageContent(1000)
  };
}